/* eslint-disable no-restricted-syntax */

import type {
  PersistedErrorInfo,
  MetaResponse,
  ErrorResponse,
  GraphQlResponse,
  Fetch,
} from './types';

// Apollo Link REST does not add errors to the array of errors for partial
// issues like a 404 or a 409. Instead, the error is dropped.
//
// This file uses a terrible, terrible hack that verifies whether the error is
// going to be dropped by Apollo, and stores it in an object with a single
// `__typename` key, so that Apollo Link REST does not remove it when selecting
// the query fields from the REST response and thus we can read it back below.
//
// Then, the data is later rebuilt by scanning `__typenames` pointing to
// objects.

const wrappedCache = new WeakMap<Fetch, Fetch>();

// Generating a `Response` object to wrap a fetch error requires an HTTP code
// in the [200, 599] range, so we use an unused code to avoid conflicts with
// other legitimate codes.
export const NETWORK_ERROR = 457;

// Even weirder, to emulate GraphQL we need to create a success code that wraps
// the error (GraphQL returns HTTP 200 with errors inside and that's OK, but
// since this is "fake" GraphQL engineers get confused when they see a response
// code of 257).
export const NETWORK_SUCCESS = 257;

export function fetchWrapper(fetch: null): null;
export function fetchWrapper(fetch: Fetch): Fetch;
export function fetchWrapper(fetch: Fetch | null): Fetch | null {
  if (!fetch) {
    return fetch;
  }

  const wrappedFetch = wrappedCache.get(fetch);

  if (wrappedFetch) {
    return wrappedFetch;
  }

  const newWrappedFetch: Fetch = async function withErrorConversion(
    url,
    options = {},
    ...args
  ) {
    let response;
    let responseText: string | null = null;

    try {
      response = await fetch(url, options);

      // Parse the response as text. This may fail with a TypeError too if the
      // stream is broken and we want to catch it as a network error (see
      // https://fetch.spec.whatwg.org/#concept-body-consume-body).
      responseText = await response.text();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';

      const data = {
        code: 'network_error',
        message: `Native fetch error: ${message}`,
      };

      responseText = JSON.stringify(data);
      response = new Response(responseText, {status: NETWORK_ERROR});
    }

    let isValidJsonButNotObject = false;

    try {
      const parsedResponse = JSON.parse(responseText);
      isValidJsonButNotObject = typeof parsedResponse !== 'object';
    } catch (e) {
      // Silent failure in case response is not a valid JSON.
    }

    //
    if (
      !response.ok ||
      // We want to return errors when response is a valid JSON but not an object.
      // That response would not be a valid GraphQL response anyway and returning error
      // here is easier than tweaking the RestLink internals.
      // Look at http://go/pr/1232171 for more details.
      isValidJsonButNotObject
    ) {
      let body;

      // @ts-expect-error Manually modified apollo-link-rest to pass field as an extra arg.
      const field: string = args[0];

      try {
        const parsed = responseText ? JSON.parse(responseText) : {};
        body = typeof parsed === 'object' && parsed !== null ? parsed : {};
      } catch (err) {
        body = {};
      }

      const requestId = response.headers?.get('Request-Id');

      const info: PersistedErrorInfo = {
        body,

        meta: {
          field,
          status: response.status === NETWORK_ERROR ? -1 : response.status,
          requestId,
          url,
        },
      };

      return new Response(JSON.stringify({__typename: info}), {
        status: NETWORK_SUCCESS,
        statusText: response.statusText,
        headers: response.headers,
      });
    }

    return new Response(responseText, {
      headers: response.headers,
      status: response.status,
      statusText: response.statusText,
    });
  };

  wrappedCache.set(fetch, newWrappedFetch);

  return newWrappedFetch;
}

type RebuildErrorFromResponse<GraphQlError> = (
  maybeBody: ErrorResponse | null,
  maybeMeta: MetaResponse,
) => GraphQlError;

export function rebuildResponse<GraphQlError>(
  res: GraphQlResponse<GraphQlError>,
  rebuildErrorFromResponse: RebuildErrorFromResponse<GraphQlError>,
): GraphQlResponse<GraphQlError> {
  const {data, errors} = res;
  const allErrors = [...(errors || [])];

  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const maybeResponse = data[key]?.__typename;

      if (typeof maybeResponse === 'object') {
        if (maybeResponse.body?.errors) {
          maybeResponse.body.errors.forEach((error: ErrorResponse) => {
            allErrors.push(rebuildErrorFromResponse(error, maybeResponse.meta));
          });
        } else {
          allErrors.push(
            rebuildErrorFromResponse(maybeResponse.body, maybeResponse.meta),
          );
        }

        data[key] = null;
      }
    }
  }

  return {
    data,
    errors: allErrors.length ? allErrors : undefined,
  };
}
